import React from 'react';
import axios from 'axios';
import * as firebase from 'firebase/app';
import 'firebase/performance';
import 'firebase/auth';
import 'firebase/firestore';
import * as Sentry from '@sentry/browser';
import { Notifications } from '@econ/ui-elements';
import ReactGA from 'react-ga';
import Firebird from '../../../legacy/Firebird';
import Startup from './Startup';
import { EContext, EContextProps } from '../../shared/components/EContext';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import UnexpectedError from '../../pages/errors/UnexepectedError/UnexpectedError';
import routeTextsMap from '../../../utilities/routeTextsMap';
import internalApiClient from '../../shared/util/api/internalApiClient';
import { Provider as DataProvider } from '../../shared/components/DataContext';
import { setupMarketEndpoint } from '../../shared/util/api/marketApiClient';
import { setupAddonsEndpoint } from '../../shared/util/api/addonsApiClient';
import getEnvironment from '../../../utilities/getEnvironment';
import pkg from '../../../../package.json';
import { Localization, Provider as GaudiProvider } from '@economic/gaudi';

const clientEnv = getEnvironment();
const version = pkg.version;
const isStaging = clientEnv === 'staging';
const isProduction = clientEnv === 'production';

interface Props {
    environment: string;
    history: any;
    version: string;
}

interface State {
    context: Pick<EContextProps, Exclude<EContextProps, { refreshRouting }>>;
    gaudiLocalization?: Localization;
    error?: Error;
    loading: boolean;
}

declare global {
    interface Window {
        gotoRoute: Function;
        refreshRouting: Function;
        attachDocumentToEntry: Function;
    }
}

class Economic extends React.Component<Props, State> {
    state = {
        context: {
            identity: undefined,
            routes: undefined,
            texts: undefined,
        },
        gaudiLocalization: undefined,
        error: undefined,
        loading: true,
    };

    constructor(props: Props) {
        super(props);

        // initialize sentry before we do any mounting and requests
        Sentry.init({
            dsn: 'https://8b353576c1374574a12eae05d77473ee@sentry.e-conomic.net/21',
            release: version,
            environment: clientEnv,
            beforeSend: event => {
                if (isProduction || isStaging) {
                    return event;
                }

                return null;
            },
        });

        // provides an integration for legacy e-conomic to trigger route changes
        window.top.gotoRoute = this.props.history.push;
        window.top.refreshRouting = this.refreshRouting;
    }

    async componentDidMount() {
        let currentIdentity;

        try {
            const xsrftoken = await internalApiClient.get('/application/anti-forgery').then(response => response.data); // get anti forgery tokens
            const antiForgeryHeaders = {
                headers: {
                    'economic-xsrf': xsrftoken,
                },
            };
            const [identity, routes, configuration] = await Promise.all([
                internalApiClient.get('/application/identity', antiForgeryHeaders).then(response => response.data),
                internalApiClient.get('/application/routes', antiForgeryHeaders).then(response => response.data),
                internalApiClient.get('/application/configuration', antiForgeryHeaders).then(response => response.data),
            ]);

            setupMarketEndpoint(configuration.marketApiEndpoint);
            setupAddonsEndpoint(configuration.addonsApiEndpoint);

            try {
                // setup firestore
                firebase.initializeApp({
                    projectId: configuration.firestoreProjectId,
                    authDomain: configuration.firestoreAuthDomain,
                    apiKey: configuration.firestoreApiKey,
                    appId: configuration.firestoreAppId,
                });

                if (isStaging || isProduction) {
                    (firebase as any).performance();
                }
            } catch {
                // firestore should not kill the client
            }

            currentIdentity = identity;

            // configure sentry as early as possible, now that we know who the user is
            Sentry.configureScope(scope => {
                scope.setUser({
                    id: `${identity.currentAgreement.number}_${identity.user.id}`,
                    User: identity.user.id,
                    'Agreement Number': identity.currentAgreement.number,
                });

                scope.setTag('administrator', identity.user.isAdministrator);
            });

            if (isProduction) {
                // @ts-ignore
                ReactGA.initialize('UA-126515006-1', {
                    gaOptions: {
                        sampleRate: 50, // https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#sampleRate
                    },
                });

                ReactGA.set({
                    anonymizeIp: true,
                    language: identity.locale.language.code,
                    userId: identity.user.guid,
                });

                this.props.history.listen(location => {
                    // page titles cannot be sent to GA because they contain agreement number and company name, which breaks GDPR
                    ReactGA.pageview(location.pathname, undefined, `Page - ${location.pathname}`);
                });
            }

            // TODO: https://jira.visma.com/browse/EC-19344
            const texts = await axios
                .post(
                    `${process.env.REACT_APP_ECONOMIC_PATH}/internal-api/texts/${identity.locale.language.code}`,
                    this.getTextIds()
                )
                .then(response => response.data);

            window.top.document.title = `${identity.currentAgreement.number} - ${identity.currentAgreement.name}`;

            const gaudiLocalization = this.getGaudiLocalization(identity, texts);

            this.setState({
                context: {
                    identity,
                    routes,
                    texts: texts || {},
                },
                gaudiLocalization,
                loading: false,
            });
        } catch (error) {
            if (!error || (error.response && error.response.status === 401)) {
                // the api returns the appropriate login url for redirection
                window.location.replace(
                    error.response.data + '?returnUrl=' + encodeURIComponent(encodeURIComponent(window.location.href))
                );
            } else {
                Sentry.captureException(error);
                this.setState({ error, loading: false });
            }
        }

        try {
            if (!firebase.apps.length) {
                // if no applications authenticated
                return;
            }

            const firestoreToken = await internalApiClient.get('/application/firebase/token').then(response => response.data);
            await firebase.auth().signInWithCustomToken(firestoreToken);

            // ensure agreement document exists, or every subsequent query will return nothing
            const db = firebase.firestore();
            const agreement = await db.collection('agreements').doc(String(currentIdentity!.currentAgreement.number));
            const agreementDocument = await agreement.get();

            if (!agreementDocument.exists) {
                await agreement.set({});
                await agreement
                    .collection('users')
                    .doc(currentIdentity!.user.guid)
                    .set({});
            }
        } catch {
            // firestore should not kill the client
        }
    }

    render() {
        if (this.state.error) return <UnexpectedError />;
        if (this.state.loading) return null;

        return (
            <EContext.Provider value={{ ...this.state.context, refreshRouting: this.refreshRouting }}>
                <GaudiProvider localization={this.state.gaudiLocalization}>
                    <DataProvider>
                        <Startup>{this.props.children}</Startup>
                    </DataProvider>
                </GaudiProvider>
            </EContext.Provider>
        );
    }

    refreshRouting = async () => {
        const routes = await internalApiClient.get('/application/routes').then(response => response.data);
        const identity = await internalApiClient.get('/application/identity').then(response => response.data);

        this.setState(state => ({
            ...state,
            context: {
                ...state.context,
                identity,
                routes,
            },
        }));
    };

    getGaudiLocalization = (identity, texts): Localization | undefined => {
        if (!identity || !texts) {
            return undefined;
        }

        return {
            locale: identity.locale.language.code,
            texts: {
                calendar: {
                    weekdaysShort: {
                        monday: texts['SHARED_WEEKDAY_SHORT_MONDAY'],
                        tuesday: texts['SHARED_WEEKDAY_SHORT_TUESDAY'],
                        wednesday: texts['SHARED_WEEKDAY_SHORT_WEDNESDAY'],
                        thursday: texts['SHARED_WEEKDAY_SHORT_THURSDAY'],
                        friday: texts['SHARED_WEEKDAY_SHORT_FRIDAY'],
                        saturday: texts['SHARED_WEEKDAY_SHORT_SATURDAY'],
                        sunday: texts['SHARED_WEEKDAY_SHORT_SUNDAY'],
                    },
                    actions: {
                        previousMonth: texts['CALENDAR_ACTIONS_PREVIOUSMONTH'],
                        nextMonth: texts['CALENDAR_ACTIONS_NEXTMONTH'],
                        previousYear: texts['CALENDAR_ACTIONS_PREVIOUSYEAR'],
                        nextYear: texts['CALENDAR_ACTIONS_NEXTYEAR'],
                        today: texts['CALENDAR_ACTIONS_TODAY'],
                    },
                },
            },
            formatting: {
                date: identity.locale.formatting.date,
            },
        };
    };

    getTextIds = () => {
        return [
            ...Object.values(routeTextsMap),
            'LEFTMENU_FIREBIRD_QUICKLINKS',
            'TOP_NAVIGATION_MORE_TEXT',
            'QUICK_SEARCH_NO_RESULTS',
            'QUICK_SEARCH_PLACEHOLDER',
            'AGREEMENT_SELECTOR_SEARCH_PLACEHOLDER',
            'AGREEMENT_SELECTOR_ADD_ACCOUNT',
            'AGREEMENT_SELECTOR_LOG_OUT',
            'AGREEMENT_SELECTOR_ADMINISTRATOR',
            'AGREEMENT_SELECTOR_DEVELOPER',
            'CLIENT_NOTIFICATION_INTRODUCTION_TITLE',
            'CLIENT_NOTIFICATION_INTRODUCTION_DESCRIPTION',
            'CLIENT_NOTIFICATION_INTRODUCTION_INFO',
            'SHARED_WEEKDAY_SHORT_MONDAY',
            'SHARED_WEEKDAY_SHORT_TUESDAY',
            'SHARED_WEEKDAY_SHORT_WEDNESDAY',
            'SHARED_WEEKDAY_SHORT_THURSDAY',
            'SHARED_WEEKDAY_SHORT_FRIDAY',
            'SHARED_WEEKDAY_SHORT_SATURDAY',
            'SHARED_WEEKDAY_SHORT_SUNDAY',
            'CALENDAR_ACTIONS_PREVIOUSMONTH',
            'CALENDAR_ACTIONS_NEXTMONTH',
            'CALENDAR_ACTIONS_PREVIOUSYEAR',
            'CALENDAR_ACTIONS_NEXTYEAR',
            'CALENDAR_ACTIONS_TODAY',
        ];
    };
}

export default function EconomicWrapper(props) {
    return (
        <>
            <ErrorBoundary component={UnexpectedError}>
                <Notifications>
                    <Economic {...props} />
                </Notifications>
            </ErrorBoundary>

            <Firebird />
        </>
    );
}
